import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { ToolTipFixedWidth } from 'components/common/tooltips';
import { SecondaryButton } from 'components/common/buttonsComponents';
import { setCenterPaneExtraInfoPanelActive } from 'actions';
import CWProduct from '../CWProduct/CWProduct';
import { PRODUCT_MENTIONED, RECENT_ORDER } from 'constants/commerceDetectionTypes';
import { twoBtnPopupCreator } from 'components/popups/ConfirmPopup/helper';
import * as extract from 'extractFromState';
import * as actions from 'actions/CommerceWizardActions';
import * as S from '../CommerceWizard.styles';

const CWSelect = (props) => {
  const getCurrentProductImage = () => {
    return get(props, 'customerProduct.image', null);
  };

  const getSuggestionPrice = (product) => {
    return product.price && Math.floor(product.price).toFixed(0);
  };

  const getCurrentProductPrice = () => {
    return get(props, 'customerProduct.price', '0.00');
  };

  const getCurrentProductText = () => {
    switch (props.detectionSource) {
      case PRODUCT_MENTIONED:
        return 'Product the customer mentioned';
      case RECENT_ORDER:
        return 'Product the customer recently purchased';
      default:
        return 'Product customer mentioned or recently ordered';
    }
  };

  const handleCloseClick = () => {
    return twoBtnPopupCreator({
      content: {
        main: `You have not recommended a product.`,
        subText: [`Are you sure you want to leave?`],
        iconPath: '/images/green-alert-bell.svg',
      },
      btnConfig: {
        dismiss: 'Stay',
        confirm: 'Leave',
      },
      onConfirm: () => {
        props.eraseRecommendationRegExFromTextInput();
        props.trackCommerceCloseWithNoRecommendations();
        props.setCenterPaneExtraInfoPanelActive(false);
      },
    });
  };

  return (
    <>
      {props.customerProduct && (
        <>
          <CWProduct
            headerText={getCurrentProductText()}
            image={getCurrentProductImage()}
            productLink={get(props, 'customerProduct.productLink', '')}
            description={get(props, 'customerProduct.description', '')}
            title={get(props, 'customerProduct.title', '')}
            price={getCurrentProductPrice()}
          />
          <S.DividerLine />
        </>
      )}
      <S.SectionLabel>Please select a product recommendation</S.SectionLabel>
      {!!props.crossSellProducts.length && (
        <>
          <S.SectionSubLabel>
            Products that go well with this product
            <ToolTipFixedWidth
              arrow
              placement="bottom"
              content={`Simplr scanned the messages in this conversation. These are products
              we think this customer might enjoy based on the message text, their purchase
              history and other customers' purchase histories.`}
            >
              <img src={'/images/tooltip-icon.svg'} alt="icon" />
            </ToolTipFixedWidth>
          </S.SectionSubLabel>
          <S.SelectProductWrapper>
            {props.crossSellProducts.slice(0, 3).map((product, idx) => (
              <S.SelectProduct key={`crossSell_${idx}`} onClick={() => props.setCommerceWizardSelectedProduct(product)}>
                <S.SelectProductImage url={product.image} />
                <div>
                  {getSuggestionPrice(product) && <h3>${getSuggestionPrice(product)}</h3>}
                  <h4>See details</h4>
                </div>
              </S.SelectProduct>
            ))}
          </S.SelectProductWrapper>
        </>
      )}
      {!!props.upSellProducts.length && (
        <>
          <S.SectionSubLabel>
            Alternatives to the current product
            <ToolTipFixedWidth
              arrow
              placement="bottom"
              content={`Simplr scanned the messages in this conversation. These are products
              we think this customer might enjoy based on the message text, their purchase
              history and other customers' purchase histories.`}
            >
              <img src={'/images/tooltip-icon.svg'} alt="icon" />
            </ToolTipFixedWidth>
          </S.SectionSubLabel>
          <S.SelectProductWrapper>
            {props.upSellProducts.slice(0, 3).map((product, idx) => (
              <S.SelectProduct key={`upSell_${idx}`} onClick={() => props.setCommerceWizardSelectedProduct(product)}>
                <S.SelectProductImage url={product.image} />
                <div>
                  {getSuggestionPrice(product) && <h3>${getSuggestionPrice(product)}</h3>}
                  <h4>See details</h4>
                </div>
              </S.SelectProduct>
            ))}
          </S.SelectProductWrapper>
        </>
      )}
      {!!props.globalProducts.length && (
        <>
          <S.SectionSubLabel>
            These products are popular with customers
            <ToolTipFixedWidth
              arrow
              placement="bottom"
              content={`This is a list of suggestions selected by the partner. It's always a good time to tell customers about popular products!`}
            >
              <img src={'/images/tooltip-icon.svg'} alt="icon" />
            </ToolTipFixedWidth>
          </S.SectionSubLabel>
          <S.SelectProductWrapper>
            {props.globalProducts.slice(0, 3).map((product, idx) => (
              <S.SelectProduct key={`crossSell_${idx}`} onClick={() => props.setCommerceWizardSelectedProduct(product)}>
                <S.SelectProductImage url={product.image} />
                <div>
                  {getSuggestionPrice(product) && <h3>${getSuggestionPrice(product)}</h3>}
                  <h4>See details</h4>
                </div>
              </S.SelectProduct>
            ))}
          </S.SelectProductWrapper>
        </>
      )}
      <S.NotHelpfulWrapper>
        <p>
          If none of these suggestions are helpful press close
          <ToolTipFixedWidth
            arrow
            placement="bottom"
            content={`Usually our recommendations are great!...but occasionally we get it wrong.
            If none of these recommendations seem appropriate, you can simply exit the process.`}
          >
            <img src={'/images/tooltip-icon.svg'} alt="icon" />
          </ToolTipFixedWidth>
        </p>
        <SecondaryButton onClick={() => handleCloseClick()}>Close</SecondaryButton>
      </S.NotHelpfulWrapper>
    </>
  );
};

CWSelect.propTypes = {
  customerProduct: PropTypes.object,
  crossSellProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productLink: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    })
  ).isRequired,
  upSellProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productLink: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    })
  ).isRequired,
  globalProducts: PropTypes.arrayOf(
    PropTypes.shape({
      productLink: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    })
  ).isRequired,
  setCommerceWizardPath: PropTypes.func.isRequired,
  setCommerceWizardSelectedProduct: PropTypes.func.isRequired,
  eraseRecommendationRegExFromTextInput: PropTypes.func.isRequired,
  setCenterPaneExtraInfoPanelActive: PropTypes.func.isRequired,
  detectionSource: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  customerProduct: extract.extractCommerceWizardProductDetected(state),
  crossSellProducts: extract.extractCommerceWizardCrossSellProducts(state),
  upSellProducts: extract.extractCommerceWizardUpsSellProducts(state),
  globalProducts: extract.extractCommerceWizardGlobalProduct(state),
  detectionSource: extract.extractCommerceWizardDetectionSource(state),
});

const mapDispatchToProps = {
  setCommerceWizardPath: actions.setCommerceWizardPath,
  setCommerceWizardSelectedProduct: actions.setCommerceWizardSelectedProduct,
  eraseRecommendationRegExFromTextInput: actions.eraseRecommendationRegExFromTextInput,
  setCenterPaneExtraInfoPanelActive,
  trackCommerceCloseWithNoRecommendations: actions.trackCommerceCloseWithNoRecommendations,
};

export default connect(mapStateToProps, mapDispatchToProps)(CWSelect);
